import { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const refundErrorMessage = (error) => {
  const data = error.response && error.response.data;
  return data ? [].concat(data).join(', ') : 'Could not process refund. Please try again.';
};

export default function useOrderRefund({ csrfToken }) {
  const [refund, setRefund] = useState({});
  const [submittingRefund, setSubmittingRefund] = useState(false);

  const refundChanged = (next) => setRefund(next || {});

  const fetchNewRefund = (token, team, order, ticket, prefillAmount = null, onPrefillFailed = null) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = token || csrfToken;
    const base = `/teams/${team.id}/orders/${order.id}/refunds/new`;
    const url = ticket && ticket.barcode ? `${base}?ticket_id=${ticket.barcode}` : base;

    return axios.get(url).then(({ data }) => {
      const partialRefunds = [...data.partial_refunds].map((pf) => {
        if (pf.amount && String(pf.amount).length > 0) {
          const parsed = parseFloat(pf.amount);
          return { ...pf, quantity: parsed > 0 ? 1 : 0, amount: parsed };
        }
        return pf;
      });
      const startAmount = data.amount && String(data.amount).length > 0 ? parseFloat(data.amount) : null;
      if (prefillAmount !== null && ticket) {
        const target = partialRefunds.find((pf) => pf.ticket_id === ticket.id);
        if (!target || !(target.ticket && target.ticket.eligible_for_refund) || prefillAmount <= 0 || prefillAmount >= parseFloat(target.max_amount)) {
          if (onPrefillFailed) onPrefillFailed();
          return;
        }
        const amount = Math.round(prefillAmount * 100) / 100;
        const prefilled = partialRefunds.map((pf) => (pf.ticket_id === ticket.id ? { ...pf, quantity: 1, amount } : pf));
        refundChanged({ ...data, amount, partial_refunds: prefilled });
        return;
      }
      refundChanged({ ...data, amount: startAmount, partial_refunds: partialRefunds });
    }).catch((error) => {
      if (onPrefillFailed) return onPrefillFailed();
      return toast.error(refundErrorMessage(error), { position: toast.POSITION.TOP_CENTER, hideProgressBar: true, autoClose: 5000 });
    });
  };

  const createRefund = (token, team, order, confirm, current) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = token || csrfToken;
    setSubmittingRefund(true);

    const partialRefundsAttributes = current.partial_refunds
      .filter((pr) => pr.quantity && pr.quantity === 1 && pr.ticket.eligible_for_refund)
      .map((pr) => ({
        ticket_id: pr.ticket_id,
        max_amount: pr.max_amount,
        amount: pr.amount,
        order_id: order.id,
        amount_refunded_to_stripe: pr.amount_refunded_to_stripe,
        amount_refunded_to_giftcard: pr.amount_refunded_to_giftcard,
      }));

    return axios.post(`/teams/${team.id}/orders/${order.id}/refunds`, {
      refund: {
        reason: current.reason,
        max_amount: current.max_amount,
        amount: current.amount,
        partial_refunds_attributes: partialRefundsAttributes,
      },
    })
      .then(({ data }) => refundChanged(data))
      .catch((error) => toast.error(refundErrorMessage(error), { position: toast.POSITION.TOP_CENTER, hideProgressBar: true, autoClose: 5000 }))
      .then(() => setSubmittingRefund(false));
  };

  const confirmRefund = (token, team, order, current) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = token || csrfToken;
    setSubmittingRefund(true);

    return axios.post(`/teams/${team.id}/orders/${order.id}/refunds/${current.id}/confirm`, {
      refund: { financial_connection_account_id: current.financial_connection_account_id },
    })
      .then(({ data }) => {
        refundChanged(data);
        toast.success('Refund in progress...', { position: toast.POSITION.TOP_CENTER, hideProgressBar: true, autoClose: 5000 });
      })
      .catch((error) => toast.error(refundErrorMessage(error), { position: toast.POSITION.TOP_CENTER, hideProgressBar: true, autoClose: 5000 }))
      .then(() => setSubmittingRefund(false));
  };

  return { refund, submittingRefund, refundChanged, fetchNewRefund, createRefund, confirmRefund };
}
